import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import styled from 'styled-components';
import { setPlayingChannel } from '../../actions/playingChannel';
import { useLang } from '../../language/LanguageProvider.jsx';
import { media } from '../../other/breakpoints';
import { convertRemToPixels, convertVhToPixels } from '../../other/convert-rem';
import Popup from '../Popup/Popup';
import Channel from './Channel';

const ChannelsList = styled.ul`
  height: calc(100vh - 20rem);
  padding: 1rem 0.6rem 0.8rem 0.6rem;
  background-color: var(--first-color);
  list-style-type: none;
  border-top: 1px solid #000;
  box-shadow: 0 7px 14px rgb(50 50 93 / 10%), 0 3px 6px rgb(0 0 0 / 8%);
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  ${media.mobile} {
    padding: 1rem 0.5rem 0.5rem 0.5rem;
    height: auto;
    min-height: 40vh;
    max-height: none;
    overflow: visible;
  }

  &:focus {
    outline-width: 0px;
  }
`;

const Channels = ({ playlist }) => {
  const { t } = useLang();
  const playingChannel = useSelector((state) => state.playingCh);

  const query = useQuery();
  const searchText = query.get('search');

  const [showPopup, setShowPopup] = useState(false);
  const [channelsPlaylist, setChannelsPlaylist] = useState(playlist);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const listRef = useRef();
  const mainScrollPositionRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setRefreshKey((prev) => prev + 1);
      if (listRef.current) {
        listRef.current.resetAfterIndex(0);
      }
    };

    const handleOrientationChange = () => {
      setTimeout(() => {
        setRefreshKey((prev) => prev + 1);
        if (listRef.current) {
          listRef.current.resetAfterIndex(0);
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (playingChannel && listRef.current && channelsPlaylist.length > 0) {
      const channelIndex = channelsPlaylist.findIndex(
        (ch) => ch.stream_id === playingChannel.stream_id
      );

      if (channelIndex >= 0) {
        // Salvar posição de scroll da página antes de fazer scroll
        mainScrollPositionRef.current =
          window.pageYOffset || document.documentElement.scrollTop;

        // Usar scrollToItem do React Window para scrollar o item na lista virtual
        listRef.current.scrollToItem(channelIndex, 'center');

        // Aguardar renderização antes de focar
        setTimeout(() => {
          // Restaurar posição de scroll da página para evitar que o player seja empurrado
          window.scrollTo(0, mainScrollPositionRef.current);
          focusChannelWithoutScroll();
        }, 100);
      }
    }
  }, [playingChannel, channelsPlaylist]);

  useEffect(() => {
    setChannelsPlaylist(playlist);
    if (playingChannel) {
      setTimeout(() => {
        const channelIndex = playlist.findIndex(
          (ch) => ch.stream_id === playingChannel.stream_id
        );
        if (channelIndex >= 0 && listRef.current) {
          mainScrollPositionRef.current =
            window.pageYOffset || document.documentElement.scrollTop;
          listRef.current.scrollToItem(channelIndex, 'center');
          setTimeout(() => {
            window.scrollTo(0, mainScrollPositionRef.current);
            focusChannelWithoutScroll();
          }, 100);
        }
      }, 100);
    }
  }, [playlist]);

  useEffect(() => {
    const searched = searchText
      ? playlist.filter((x) =>
          x.name.toLowerCase().includes(searchText.toLowerCase())
        )
      : playlist;
    if (searched.length > 0) {
      setChannelsPlaylist(searched);
      if (playingChannel) {
        setTimeout(() => {
          const channelIndex = searched.findIndex(
            (ch) => ch.stream_id === playingChannel.stream_id
          );
          if (channelIndex >= 0 && listRef.current) {
            mainScrollPositionRef.current =
              window.pageYOffset || document.documentElement.scrollTop;
            listRef.current.scrollToItem(channelIndex, 'center');
            setTimeout(() => {
              window.scrollTo(0, mainScrollPositionRef.current);
              focusChannelWithoutScroll();
            }, 100);
          }
        }, 100);
      }
    } else if (searchText) setShowPopup(1);
    else history.push('/live/category/');
  }, [searchText]);

  const focusChannelWithoutScroll = () => {
    const selectedElement = document.getElementById('selectedCh');
    const fallbackElement = document.querySelector('.channel');

    if (selectedElement || fallbackElement) {
      // Prevenir scroll automático ao focar
      const elementToFocus = selectedElement || fallbackElement;
      const scrollPos =
        window.pageYOffset || document.documentElement.scrollTop;

      // Focar o elemento (verificar se existe antes)
      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        try {
          elementToFocus.focus({ preventScroll: true });
        } catch (e) {
          try {
            elementToFocus.focus();
          } catch (e2) {
            // Ignorar erro silenciosamente
          }
        }
      }

      // Forçar restauração da posição de scroll após o foco
      setTimeout(() => {
        window.scrollTo(0, scrollPos);
      }, 0);
    }
  };

  const focusChannel = () => {
    focusChannelWithoutScroll();
  };

  const Row = ({ index, style }) => (
    <Channel
      key={channelsPlaylist[index].stream_id}
      Name={channelsPlaylist[index].name}
      Shift={channelsPlaylist[index].shift}
      Epg={channelsPlaylist[index].epg_channel_id}
      id={channelsPlaylist[index].stream_id}
      Image={channelsPlaylist[index].stream_icon}
      Number={channelsPlaylist[index].num}
      selectEvent={() => {
        dispatch(setPlayingChannel(channelsPlaylist[index]));
      }}
      virtualScrollStyle={style}
      selected={
        playingChannel &&
        playingChannel.stream_id === channelsPlaylist[index].stream_id
      }
    />
  );

  const listHeight = convertVhToPixels('100vh') - convertRemToPixels(20);

  return (
    <>
      <ChannelsList tabIndex={0}>
        <List
          key={refreshKey}
          height={listHeight}
          itemCount={channelsPlaylist.length}
          itemSize={convertRemToPixels(5)}
          style={{ overflowY: 'auto', height: '100%' }}
          useIsScrolling={true}
          ref={listRef}>
          {Row}
        </List>
      </ChannelsList>
      {showPopup && (
        <Popup
          title={t('main.noStreamTitle', { text: searchText })}
          description={t('main.noStreamDescription')}
          icon={'fas fa-search'}
          onclick={() => {
            setShowPopup(0);
            focusChannel();
            history.replace(location.pathname.split('?')[0]);
          }}
        />
      )}
    </>
  );
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default Channels;
